import { useEffect, useId, useRef, useState } from 'react';

/**
 * Opening a menu and walking it with the keyboard, for the option blocks' trigger pickers.
 *
 * Six blocks each had their own copy of this — Keyword, MCQ, Dropdown, Ordering, Scale and FITB
 * — and the six were byte-identical in behaviour: the same open state, the same blur-to-close,
 * the same Arrow/Home/End/Escape handler, the same focus-the-selected-option-on-open, the same
 * focus-returns-to-the-trigger. Six implementations of one behaviour that nothing held together.
 *
 * ## What is NOT here, deliberately
 *
 * The DRAWING. These six look different for reasons that are each load-bearing, and a shared
 * component would have had to absorb all of them:
 *
 *   - every option carries an EVALUATION — a check, a cross, a dash, or a blank spacer where
 *     there is no verdict at all, with the trigger tinted to match
 *   - the menu lists positional names ("Option 2") while the trigger shows the real content,
 *     because an equation or an image label would otherwise blow the menu open
 *   - that content renders as CHIPS, not text, so a variable reads as a variable
 *   - a media option is bracketed behind an icon, so a filename never reads as student-facing text
 *   - MCQ slashes the whole control for an `_absent` trigger, and its own note explains that
 *     slashing just the option would say the opposite thing
 *   - a separator divides the graded options from the extra ones
 *
 * So this shares the POINTER AND KEYBOARD WORK and nothing else, the same division `useDialDrag`
 * draws for the simulator dials and for the same reason: a shared thing that takes a `mode` or a
 * `renderOption` prop to cover six callers has not removed the six cases, only moved them where
 * they are harder to read.
 *
 * ## It levels the six UP, not down
 *
 * They had drifted, and not evenly. The Dropdown block's picker had grown the full combobox
 * contract — `role='combobox'`, `aria-controls` to the popup, and a trigger key handler so that
 * Down opens it — with a note saying the role cannot be made honest without the last of those: a
 * listener told "combo box" knows Down opens one, presses Down, and got nothing on the other
 * five. Scale had drifted the other way, to `useLayoutEffect`, which changed nothing because the
 * `requestAnimationFrame` inside defers to the next frame regardless.
 *
 * So the hook carries Dropdown's version and the other five gain it. That is the argument for
 * doing this at all: six copies meant the best of them could not reach the others.
 */
export function useListboxMenu({ selectedIndex }: {
  /**
   * Which option to focus when the menu opens. Negative or out of range falls back to the first,
   * which is what every caller wanted for a trigger whose value is not in its own list yet.
   */
  selectedIndex: number;
}) {
  const [open, setOpen] = useState(false);
  /** Ties the trigger to its popup through `aria-controls`, which is what makes `role='combobox'`
   *  a promise rather than a label. */
  const listboxId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /**
   * Focus moves into the menu as it opens, on the NEXT frame.
   *
   * Not synchronously: the options do not exist until React has rendered the open state, so a
   * focus call in the same tick lands on nothing and the first arrow press starts from the top
   * instead of from the current choice.
   */
  useEffect(() => {
    if (!open) return;
    const idx = selectedIndex >= 0 ? selectedIndex : 0;
    const id = requestAnimationFrame(() => { optionRefs.current[idx]?.focus(); });
    return () => cancelAnimationFrame(id);
  // `selectedIndex` is deliberately not a dependency: this is where focus goes when the menu
  // OPENS, and re-running it on a value change would steal focus mid-interaction.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Collapse and hand focus back, so the keyboard does not end up at the top of the document. */
  function closeAndFocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onListboxKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
    // Read off the live refs rather than the option list: a caller may render separators or skip
    // entries, so the DOM is the only authority on what is actually focusable.
    const refs = optionRefs.current.filter((r): r is HTMLButtonElement => r !== null);
    const i = refs.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); refs[Math.min(i + 1, refs.length - 1)]?.focus(); break;
      case 'ArrowUp':   e.preventDefault(); refs[Math.max(i - 1, 0)]?.focus(); break;
      case 'Home':      e.preventDefault(); refs[0]?.focus(); break;
      case 'End':       e.preventDefault(); refs[refs.length - 1]?.focus(); break;
      case 'Escape':    e.preventDefault(); closeAndFocus(); break;
    }
  }

  /**
   * Down (or Up) opens the menu from the trigger.
   *
   * `onListboxKeyDown` lives on the popup and only runs once it is already open, so without this
   * a keyboard user who has just been told "combo box" — and therefore knows Down opens one —
   * pressed Down and got nothing.
   *
   * Enter and Space are deliberately absent: a native `<button>` already activates on both, and
   * intercepting them here would re-implement what the browser does correctly.
   */
  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (open || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    setOpen(true);
  }

  /** Closes when focus leaves the whole control — the trigger and the menu together, which is why
   *  it sits on the wrapper rather than on either one. */
  function onWrapperBlur(e: React.FocusEvent<HTMLDivElement>) {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
  }

  return {
    open,
    setOpen,
    listboxId,
    wrapperRef,
    triggerRef,
    onTriggerKeyDown,
    /** Spread onto each option button: `ref={menu.optionRef(i)}`. */
    optionRef: (i: number) => (el: HTMLButtonElement | null) => { optionRefs.current[i] = el; },
    onListboxKeyDown,
    onWrapperBlur,
    closeAndFocus,
  };
}
